/**
 * Multi-size buy: qty per variant + bulk add to cart via /cart/add.js items[].
 */
(() => {
  if (window.customElements.get("multi-size-buy")) return;

  class MultiSizeBuy extends HTMLElement {
    #submitting = false;

    connectedCallback() {
      this._onClick = this._onClick.bind(this);
      this._onQtyChange = this._onQtyChange.bind(this);
      this.addEventListener("click", this._onClick);
      this.addEventListener("change", this._onQtyChange);
      this.addEventListener("input", this._onQtyChange);
      this._updateSummary();
    }

    disconnectedCallback() {
      this.removeEventListener("click", this._onClick);
      this.removeEventListener("change", this._onQtyChange);
      this.removeEventListener("input", this._onQtyChange);
    }

    get #inputs() {
      return Array.from(this.querySelectorAll(".quantity-selector__input:not([disabled])"));
    }

    get #summary() {
      return this.querySelector(".multi-size-buy__summary");
    }

    get #error() {
      return this.querySelector(".multi-size-buy__error");
    }

    get #submit() {
      return this.querySelector("[name='add']");
    }

    _onClick(event) {
      if (event.target.closest("[name='add']")) {
        event.preventDefault();
        this._addSelected();
      }
    }

    _onQtyChange(event) {
      if (!event.target.classList?.contains("quantity-selector__input")) return;
      const value = Math.max(0, Math.floor(Number(event.target.value) || 0));
      if (String(value) !== event.target.value) {
        event.target.value = String(value);
      }
      this._updateSummary();
      this._clearError();
    }

    _selectedItems() {
      return this.#inputs
        .map((input) => {
          const quantity = Math.max(0, Math.floor(Number(input.value) || 0));
          if (quantity <= 0) return null;
          return {
            id: Number(input.dataset.variantId),
            quantity,
            price: Number(input.dataset.price) || 0
          };
        })
        .filter(Boolean);
    }

    _formatMoney(cents) {
      const amount = (Number(cents) || 0) / 100;
      try {
        const currency = window.Shopify?.currency?.active || "AUD";
        const locale = window.Shopify?.locale || "en-AU";
        return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount);
      } catch (_) {
        return "$" + amount.toFixed(2);
      }
    }

    _updateSummary() {
      const items = this._selectedItems();
      const count = items.reduce((sum, item) => sum + item.quantity, 0);
      const total = items.reduce((sum, item) => sum + item.quantity * item.price, 0);
      const summary = this.#summary;
      if (!summary) return;

      const countEl = summary.querySelector("[data-summary-count]");
      const totalEl = summary.querySelector("[data-summary-total]");
      if (countEl) countEl.textContent = String(count);
      if (totalEl) totalEl.textContent = this._formatMoney(total);

      summary.hidden = count <= 0;

      const submit = this.#submit;
      if (submit) {
        submit.toggleAttribute("disabled", count <= 0);
        submit.classList.toggle("button--subdued", count <= 0);
      }
    }

    _clearError() {
      const error = this.#error;
      if (!error) return;
      error.hidden = true;
      error.textContent = "";
    }

    _showError(message) {
      const error = this.#error;
      if (!error) return;
      error.hidden = false;
      error.textContent = message || "Unable to add to cart.";
    }

    async _addSelected() {
      if (this.#submitting) return;

      const items = this._selectedItems().map(({ id, quantity }) => ({ id, quantity }));
      if (!items.length) {
        this._showError("Select a quantity for at least one size.");
        return;
      }

      const submit = this.#submit;
      this.#submitting = true;
      submit?.setAttribute("aria-busy", "true");
      this._clearError();

      let sectionsToBundle = ["variant-added"];
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:prepare-bundled-sections", {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        })
      );

      try {
        const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest"
          },
          body: JSON.stringify({
            items,
            sections: sectionsToBundle.join(","),
            sections_url: window.location.pathname
          })
        });

        const responseJson = await response.json();

        if (!response.ok) {
          this._showError(responseJson.description || responseJson.message || "Unable to add to cart.");
          document.dispatchEvent(new CustomEvent("cart:refresh"));
          return;
        }

        if (
          window.themeVariables?.settings?.cartType === "page" ||
          window.themeVariables?.settings?.pageType === "cart"
        ) {
          window.location.href = `${window.Shopify.routes.root}cart`;
          return;
        }

        const cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
        cartContent.sections = responseJson.sections;

        this.dispatchEvent(
          new CustomEvent("variant:add", {
            bubbles: true,
            detail: {
              items: responseJson.items || [responseJson],
              cart: cartContent
            }
          })
        );

        document.documentElement.dispatchEvent(
          new CustomEvent("cart:change", {
            bubbles: true,
            detail: {
              baseEvent: "variant:add",
              cart: cartContent
            }
          })
        );

        this.#inputs.forEach((input) => {
          input.value = "0";
        });
        this._updateSummary();
      } catch (_) {
        this._showError("Unable to add to cart.");
      } finally {
        this.#submitting = false;
        submit?.removeAttribute("aria-busy");
      }
    }
  }

  window.customElements.define("multi-size-buy", MultiSizeBuy);
})();
